import { Link, useParams } from "react-router-dom";
import { specials } from "@/mocks/specials";
import { getWorkById } from "@/utils/catalog";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import WorkCard from "@/components/feature/WorkCard";
import EmptyState from "@/components/feature/EmptyState";

const accentBar: Record<string, string> = {
  primary: "bg-primary-500",
  accent: "bg-accent-500",
  secondary: "bg-secondary-500",
};

export default function SpecialDetailPage() {
  const { id } = useParams();
  const special = specials.find((s) => s.id === id);

  usePageMeta({
    title: special ? `${special.title} - ${special.subtitle} - 妻子的朋友` : "专题详情 - 妻子的朋友",
    description: special?.intro,
    keywords: special ? `${special.title},${special.subtitle},影视专题,片单推荐,妻子的朋友` : undefined,
  });

  if (!special) {
    return (
      <div className="mx-auto w-full max-w-[1360px] px-4 py-16 md:px-6">
        <EmptyState
          title="未找到该专题"
          description="这个专题可能已调整或链接有误，返回专题列表看看其他精选片单。"
        />
        <div className="mt-6 flex justify-center">
          <Link
            to="/special"
            className="flex h-11 items-center gap-2 rounded-md bg-primary-500 px-5 text-sm font-semibold text-background-50 cursor-pointer whitespace-nowrap"
          >
            返回专题合集
          </Link>
        </div>
      </div>
    );
  }

  const related = specials.filter((s) => s.id !== special.id).slice(0, 3);

  return (
    <div className="w-full">
      <div className="relative w-full">
        <div className="absolute inset-0 h-[300px] overflow-hidden md:h-[380px]">
          <img
            src={special.cover}
            alt={`${special.title}专题主图`}
            title={`${special.title} - ${special.subtitle}`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950/75 via-primary-950/65 to-background-50"></div>
        </div>

        <div className="relative mx-auto w-full max-w-[1360px] px-4 pb-2 pt-6 md:px-6 md:pt-8">
          <Breadcrumb
            tone="light"
            items={[{ label: "专题合集", to: "/special" }, { label: special.title }]}
          />
          <div className="mt-8">
            <span className={`inline-block h-1.5 w-12 rounded-full ${accentBar[special.accent]}`}></span>
            <p className="mt-3 text-xs font-semibold tracking-wider text-accent-300 uppercase">
              {special.subtitle}
            </p>
            <h1 className="mt-1 font-heading text-3xl font-bold text-background-50 md:text-4xl">
              {special.title}
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-primary-100 md:text-base">
              {special.intro}
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1360px] px-4 py-10 md:px-6 md:py-12">
        <div className="flex flex-col gap-12">
          {special.groups.map((group, gi) => {
            const groupWorks = group.workIds
              .map((wid) => getWorkById(wid))
              .filter((w): w is NonNullable<typeof w> => Boolean(w));
            return (
              <section key={group.title}>
                <div className="mb-5 flex flex-col gap-2 border-l-4 border-accent-500 pl-4">
                  <div className="flex items-center gap-2">
                    <span className="font-heading text-sm font-bold text-foreground-400">
                      0{gi + 1}
                    </span>
                    <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                      {group.title}
                    </h2>
                  </div>
                  <p className="text-sm leading-relaxed text-foreground-600">
                    <strong className="font-medium text-accent-700">推荐理由：</strong>
                    {group.reason}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                  {groupWorks.map((w, i) => (
                    <WorkCard key={w.id} work={w} index={i} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        <section className="mt-14">
          <div className="mb-5">
            <span className="mb-1 inline-flex items-center gap-1.5 text-xs font-semibold tracking-wider text-accent-600 uppercase">
              <span className="h-3 w-1 rounded-full bg-accent-500"></span>
              相关专题
            </span>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              你也许还想看
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {related.map((sp) => (
              <Link
                key={sp.id}
                to={`/special/${sp.id}`}
                className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 cursor-pointer"
              >
                <div className="h-24 w-32 shrink-0 overflow-hidden rounded-md bg-background-200">
                  <img
                    src={sp.cover}
                    alt={`${sp.title}专题封面`}
                    title={`${sp.title} - ${sp.subtitle}`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="min-w-0">
                  <h3 className="font-heading text-base font-bold text-foreground-950">
                    {sp.title}
                  </h3>
                  <p className="mt-1 text-xs text-foreground-500 line-clamp-2">{sp.subtitle}</p>
                  <span className="mt-2 flex items-center gap-1 text-xs text-primary-700">
                    进入专题
                    <span className="flex h-4 w-4 items-center justify-center transition-transform group-hover:translate-x-1">
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}